import { PageHero } from '@/components/PageHero';
import { QuoteBoard } from '@/components/fx/Schematics';
import { TerminalPanel } from '@/components/fx/TerminalPanel';
import { Reveal } from '@/components/fx/Reveal';
import { Headline } from '@/components/fx/Headline';
import { TiltCard } from '@/components/fx/TiltCard';
import type { AccessVals } from '@/lib/vals/access';

export function AccessPage({ v }: { v: AccessVals }) {
  return (
    <main id="wft-main" tabIndex={-1}>
      <PageHero
        eyebrow="Access"
        title="Run it on your own machine."
        highlight={['own', 'machine.']}
        lede="Infrastructure runs in Docker; the API, workers and frontend run natively. Whatever you leave blank in the environment file disables its connector cleanly, and the panels it would have fed say which key is missing rather than breaking."
        aside={
          <TerminalPanel mnemonic="SRC" title="Connector board">
            <QuoteBoard />
          </TerminalPanel>
        }
      />

      {/* Steps */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Getting started
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Six commands, in order."
            highlight={['Six', 'commands,']}
          />
          <ol className="steps">
            {v.steps.map((s, i) => (
              <Reveal as="li" key={s.n} delay={i * 70} className="step">
                <div className="step__head">
                  <span className="step__n">{s.n}</span>
                  <span className="step__what">{s.what}</span>
                </div>
                <pre className="step__cmd glass" tabIndex={0}>
                  <code>{s.cmd}</code>
                </pre>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Ports */}
      <section className="section">
        <div className="shell split">
          <div className="split__lead">
            <Reveal as="p" className="eyebrow">
              Ports
            </Reveal>
            <Headline
              as="h2"
              className="display display--section"
              text="Deliberately not the defaults."
              highlight={['not']}
            />
            <Reveal as="p" className="lede" delay={200}>
              The same machine may also run god-engine's stack, and a silent connection to the wrong
              database is the worst available failure mode.
            </Reveal>
          </div>
          <div className="ports">
            {v.ports.map((p, i) => (
              <Reveal key={p.svc} delay={i * 80}>
                <div className="port glass">
                  <span className="port__svc">{p.svc}</span>
                  <span className="port__num">{p.port}</span>
                  <span className="port__default">{p.default}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Environment */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Environment
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="What each key unlocks, and what it costs."
            highlight={['costs.']}
          />
          <div className="fields">
            {v.env.map((e, i) => (
              <Reveal key={e.key} delay={i * 60}>
                <div className="field field--env glass">
                  <code>{e.key}</code>
                  <p>{e.gives}</p>
                  <span className="field__cost">{e.cost}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ops + docs */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Model weights
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Fetched, verified, then moved into place."
            highlight={['verified,']}
          />
          <div className="lanes">
            {v.ops.map((o, i) => (
              <Reveal key={o.label} delay={i * 90}>
                <TiltCard className="lane glass" max={4}>
                  <span className="lane__bar" style={{ background: 'var(--gold)' }} />
                  <h3 className="lane__name">{o.label}</h3>
                  <p className="lane__body">{o.body}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>

          <Reveal className="docs" delay={160}>
            <p className="eyebrow">Documentation in the repository</p>
            <ul>
              {v.docs.map((d) => (
                <li key={d.file}>
                  <code>{d.file}</code>
                  <span>{d.what}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
